import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, OnDestroy, viewChild } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { ApplePayService } from '@df/module-apple-pay/apple-pay.service';
import { IBasketTotals } from '@df/session/api/basket/basket';
import { OperationalSafeguardsService } from 'projects/app-default/src/app/ui/operational-safeguards.service';
import { merge } from 'rxjs/internal/observable/merge';
import { ResultComponent } from '../../../../ui/api/result.component';
import { OpayoApplePay } from '../opayo-apple-pay';
import { OpayoApplePayService } from '../opayo-apple-pay.service';

@Component({
    selector: 'opayo-apple-pay-button',
    templateUrl: './opayo-apple-pay-button.html',
    styleUrl: './opayo-apple-pay-button.scss',
    imports: [ResultComponent],
    host: {
        '[class.busy]': 'inProgressStatus.busy'
    },
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpayoApplePayButtonComponent extends AbstractCheckoutPayment<OpayoApplePay.IPaymentMethod> implements OnDestroy {
    isExpress = false;
    lastShippingMethods?: ApplePay.IShippingMethod[];
    /**
     * Payment method code related to component
     */
    protected readonly methodCode = OpayoApplePay.METHOD_CODE;

    /**
     * Apple pay version required
     */
    protected readonly version = OpayoApplePay.PAYMENT_CONFIG.APPLE_PAY_VERSION;

    /**
     * Status objects for managing component state
     */
    protected readonly applePayAvailableStatus = new Status();
    protected readonly checkoutStatus = new Status();
    protected readonly inProgressStatus = new Status();
    protected readonly merchantSessionKeyStatus = new Status();

    /**
     * error during payment processing message
     */
    protected readonly paymentErrorMessage = 'There was an error processing your payment.';

    /**
     * Payment session data
     */
    private merchantSessionKey?: string;
    private sessionValidationToken?: string;

    /**
     * Services
     */
    protected readonly buttonRef = viewChild<ElementRef<HTMLElement>>('buttonRef');
    protected readonly applePayService = inject(ApplePayService);
    protected readonly opayoApplePayService = inject(OpayoApplePayService);
    private readonly operationalSafeguardsService = inject(OperationalSafeguardsService);
    private merchantSessionKeyRenewalWorker?: Worker;

    override ngOnInit(): void {
        this.subscriptions.add(
            merge(
                this.applePayAvailableStatus.subject,
                this.inProgressStatus.subject,
                this.merchantSessionKeyStatus.subject,
                this.checkoutStatus.subject
            ).subscribe(() => this.detectChanges())
        );
        super.ngOnInit();
        [this.inProgressStatus, this.checkoutStatus].forEach(status => {
            this.operationalSafeguardsService.add(status);
        });
        // we need to always have a valid merchant session key
        // we cannot optain it on apple pay button click because ApplePay session must be created by user click
        // We cannot load any extra data between button click and ApplePay session start
        this.renewMerchantSessionKey();
        // renew merchant session key every 390 seconds, default value for session key expiration is 400 seconds
        // Use web worker to keep interval running even when browser tab is inactive
        const workerBlob = new Blob([
            `setInterval(() => {
                postMessage('renew');
            }, 390000);`
        ], { type: 'text/javascript' });
        const workerUrl = URL.createObjectURL(workerBlob);
        this.merchantSessionKeyRenewalWorker = new Worker(workerUrl);
        URL.revokeObjectURL(workerUrl);

        this.merchantSessionKeyRenewalWorker.onmessage = () => this.renewMerchantSessionKey();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        [this.inProgressStatus, this.checkoutStatus].forEach(status => {
            this.operationalSafeguardsService.remove(status);
        });
        if (this.merchantSessionKeyRenewalWorker) {
            this.merchantSessionKeyRenewalWorker.terminate();
        }
    }

    /**
     * Handle Apple Pay button click
     */
    protected async onClick(event: MouseEvent): Promise<void> {
        event.preventDefault();

        if (!this.allowClick()) {
            this.handleInvalidCheckout();
            return;
        }

        await this.initiateApplePaySession();
    }

    /**
     * Initialize Apple Pay session with merchant session key
     */
    private async initiateApplePaySession(): Promise<void> {
        const paymentRequest = this.getPaymentRequest();

        if (!this.isApplePayAvailable(paymentRequest)) {
            this.onAvailableError();
            return;
        }

        if (this.isPaymentInProgress()) {
            return;
        }

        this.inProgressStatus.busy = true;

        try {
            this.startApplePaySession(paymentRequest!);
        } catch (error) {
            this.handleInitializationError(error);
        }
    }

    /**
     * Renew merchant session key
     * @returns void
     */
    private async renewMerchantSessionKey(): Promise<void> {
        const sessionKey = await this.obtainMerchantSessionKey();
        if (!sessionKey) {
            return;
        }
        this.merchantSessionKey = sessionKey;
    }

    /**
     * Obtain merchant session key from backend
     */
    private async obtainMerchantSessionKey(): Promise<string | undefined> {
        const response = await this.opayoApplePayService.getMerchantSessionKey(this.merchantSessionKeyStatus);

        if (!response.success || !response.merchantSessionKey) {
            this.setError(response.error || OpayoApplePay.ERROR_MESSAGES.SESSION_KEY_FAILED);
            this.inProgressStatus.busy = false;
            return undefined;
        }

        return response.merchantSessionKey;
    }

    /**
     * Start Apple Pay session
     */
    private startApplePaySession(paymentRequest: ApplePay.IPaymentRequest): void {
        const applePaySessionInstance = new window.ApplePaySession!(this.version, paymentRequest);
        this.addApplePaySessionInstanceEvents(applePaySessionInstance);
        applePaySessionInstance.begin();
    }

    /**
     * Add event handlers to Apple Pay session
     */
    protected addApplePaySessionInstanceEvents(applePaySessionInstance: ApplePay.ISessionInstance): void {
        applePaySessionInstance.onvalidatemerchant = event => {
            this.onValidateMerchant(event, applePaySessionInstance);
        };

        applePaySessionInstance.oncancel = () => {
            this.onCancel();
        };

        applePaySessionInstance.onpaymentauthorized = event => {
            this.onPaymentAuthorized(event, applePaySessionInstance);
        };
    }

    /**
     * Handle merchant validation
     */
    protected async onValidateMerchant(event: ApplePay.IValidateMerchantEvent, session: ApplePay.ISessionInstance): Promise<void> {
        const validationStatus = new Status();

        try {
            const domainName = this.extractDomainName(event.validationURL);
            const sessionResponse = await this.opayoApplePayService.getApplePaySession(domainName, validationStatus);

            if (this.isValidSessionResponse(sessionResponse)) {
                this.handleValidSession(sessionResponse, session);
            } else {
                this.handleInvalidSession(sessionResponse, session);
            }
        } catch (error) {
            this.handleMerchantValidationError(error, session);
        }
    }

    /**
     * Handle payment authorization
     */
    protected async onPaymentAuthorized(event: ApplePay.IPaymentEvent, session: ApplePay.ISessionInstance): Promise<void> {
        this.checkoutStatus.busy = true;

        try {
            this.handlePaymentResponse(event, session);
        } catch (error) {
            this.handlePaymentError(error, session);
        } finally {
            this.resetPaymentState();
        }
    }

    /**
     * Parse payment token from Apple Pay
     */
    private parsePaymentToken(token: unknown): unknown {
        return this.opayoApplePayService.parsePaymentToken(token);
    }

    /**
     * Create transaction request
     */
    private createTransactionRequest(paymentData: unknown): OpayoApplePay.ITransactionRequest {
        return {
            paymentData,
            merchantSessionKey: this.merchantSessionKey!,
            sessionValidationToken: this.sessionValidationToken,
            displayName: undefined
        };
    }

    /**
     * Handle payment response
     */
    private handlePaymentResponse(event: ApplePay.IPaymentEvent, session: ApplePay.ISessionInstance): void {
        const paymentData = this.parsePaymentToken(event.payment.token);
        const transactionRequest = this.createTransactionRequest(paymentData);

        const submissionData = this.getSubmissionData(event);

        submissionData.paymentMethod = {
            method: this.methodCode,
            additional_data: transactionRequest
        };

        this.checkoutFormComponent.submitWithData(
            submissionData,
            () => {
                this.completePaymentSuccess(session);
            },
            () => {
                this.completePaymentFailure(session);
            },
            true
        );
    }

    /**
     * Complete successful payment
     */
    private completePaymentSuccess(session: ApplePay.ISessionInstance): void {
        session.completePayment({
            status: window.ApplePaySession?.STATUS_SUCCESS || 'SUCCESS'
        });
        this.clear();
    }

    /**
     * Complete failed payment
     */
    private completePaymentFailure(session: ApplePay.ISessionInstance): void {
        session.completePayment({
            status: window.ApplePaySession?.STATUS_FAILURE || 'FAILURE'
        });
        this.checkoutStatus.error = this.paymentErrorMessage || OpayoApplePay.ERROR_MESSAGES.PAYMENT_FAILED;
        this.clear();
    }

    /**
     * Handle payment error
     */
    private handlePaymentError(error: unknown, session: ApplePay.ISessionInstance): void {
        console.error('Payment processing failed:', error);
        session.completePayment({
            status: window.ApplePaySession?.STATUS_FAILURE || 'FAILURE'
        });
        this.checkoutStatus.error = OpayoApplePay.ERROR_MESSAGES.PAYMENT_PROCESSING_FAILED;
    }

    /**
     * Reset payment state and clear sensitive data
     */
    private resetPaymentState(): void {
        this.checkoutStatus.busy = false;
        this.inProgressStatus.busy = false;
        this.merchantSessionKey = undefined;
        this.sessionValidationToken = undefined;
    }

    /**
     * Handle payment cancellation
     */
    protected onCancel(): void {
        this.inProgressStatus.busy = false;
        this.checkoutStatus.busy = false;
        this.applePayAvailableStatus.error = undefined;
    }

    /**
     * Extract domain name from validation URL
     */
    private extractDomainName(validationURL: string): string {
        return this.opayoApplePayService.extractDomainName(validationURL);
    }

    /**
     * Check if session response is valid
     */
    private isValidSessionResponse(response: OpayoApplePay.IApplePaySessionResponse): boolean {
        return this.opayoApplePayService.isValidSessionResponse(response);
    }

    /**
     * Handle valid session response
     */
    private handleValidSession(response: OpayoApplePay.IApplePaySessionResponse, session: ApplePay.ISessionInstance): void {
        session.completeMerchantValidation(response.session!);
    }

    /**
     * Handle invalid session response
     */
    private handleInvalidSession(response: OpayoApplePay.IApplePaySessionResponse, session: ApplePay.ISessionInstance): void {
        session.abort();
        this.setError(response.error || OpayoApplePay.ERROR_MESSAGES.MERCHANT_VALIDATION_FAILED);
    }

    /**
     * Handle merchant validation error
     */
    private handleMerchantValidationError(error: unknown, session: ApplePay.ISessionInstance): void {
        console.error('Merchant validation failed:', error);
        session.abort();
        this.setError(OpayoApplePay.ERROR_MESSAGES.MERCHANT_VALIDATION_FAILED);
    }

    /**
     * Handle initialization error
     */
    private handleInitializationError(error: unknown): void {
        console.error('Failed to initialize Apple Pay:', error);
        this.setError(OpayoApplePay.ERROR_MESSAGES.INIT_FAILED);
        this.inProgressStatus.busy = false;
    }

    /**
     * Handle invalid checkout
     */
    private handleInvalidCheckout(): void {
        this.checkoutFormComponent?.validateCheckout();
    }

    /**
     * Set error message
     */
    private setError(message: string): void {
        this.applePayAvailableStatus.error = message;
    }

    /**
     * Check if Apple Pay is available
     */
    private isApplePayAvailable(paymentRequest: ApplePay.IPaymentRequest | undefined): boolean {
        return this.opayoApplePayService.isApplePayAvailable() && paymentRequest !== undefined;
    }

    /**
     * Check if payment is in progress
     */
    private isPaymentInProgress(): boolean {
        return this.inProgressStatus.busy || this.checkoutStatus.busy;
    }

    /**
     * Check if click is allowed
     */
    private allowClick(): boolean {
        return this.checkoutFormComponent.isCheckoutValid;
    }

    /**
     * Get payment request data
     */
    private getPaymentRequest(): ApplePay.IPaymentRequest | undefined {
        const requestData = this.getPaymentRequestData();
        return requestData as ApplePay.IPaymentRequest;
    }

    /**
     * Build payment request data
     */
    private getPaymentRequestData(): OpayoApplePay.IApplePayPaymentRequest | undefined {
        const totals = this.getTotals();
        const merchantName = this.getMerchantName();
        const isVirtual = this.isVirtual();
        const shippingMethods = this.getExtendedShippingMethods();
        shippingMethods && (this.lastShippingMethods = shippingMethods.map(method => this.opayoApplePayService.mapShippingMethod(method)));
        return this.opayoApplePayService.buildPaymentRequest(totals, merchantName, isVirtual, this.isExpress, shippingMethods);
    }

    /**
     * Get JS assets
     */
    protected getJsAssets(): string[] {
        return [OpayoApplePay.SCRIPTS.APPLE_PAY];
    }

    /**
     * Check if Apple Pay is available
     */
    protected isAvailable(): boolean {
        return this.applePayService.isAvailable && this.applePayService.isSupportedVersion(this.version);
    }

    /**
     * Handle availability error
     */
    protected onAvailableError(): void {
        this.setError(OpayoApplePay.ERROR_MESSAGES.NOT_AVAILABLE);
    }

    /**
     * Check if preload is required
     */
    protected override preload(): boolean {
        return true;
    }

    /**
     * Handle script load success
     */
    protected override onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();

        if (!this.isAvailable()) {
            this.onAvailableError();
        }
    }

    /**
     * Get total line for payment request
     */
    protected getTotalLine(type = ApplePay.LineItemType.final): ApplePay.ILineItem | undefined {
        return this.opayoApplePayService.buildTotalLine(this.getTotals(), this.getMerchantName(), type);
    }

    /**
     * Get merchant name
     */
    private getMerchantName(): string {
        return this.method?.config?.merchantName || 'Apple Pay';
    }

    /**
     * Get basket totals
     */
    private getTotals(): IBasketTotals | undefined {
        return this.app.customer.basket.totals;
    }

    /**
     * Get line items for payment request
     */
    protected getLineItems(): ApplePay.ILineItem[] {
        const items: ApplePay.ILineItem[] = [];

        // Add basket items
        this.addBasketItems(items);

        // Add totals line items (shipping, discount)
        const totalItems = this.opayoApplePayService.buildLineItems(this.getTotals());
        items.push(...totalItems);

        return items;
    }

    /**
     * Add basket items to line items
     */
    private addBasketItems(items: ApplePay.ILineItem[]): void {
        const basket = this.app.customer.basket;

        if (!basket.items) {
            return;
        }

        basket.items.forEach(item => {
            const label = this.extractItemLabel(item);
            const amount = this.extractItemAmount(item);

            items.push({
                label,
                amount: amount.toString(),
                type: ApplePay.LineItemType.final
            });
        });
    }

    /**
     * Extract item label
     */
    private extractItemLabel(item: unknown): string {
        return this.opayoApplePayService.extractItemLabel(item);
    }

    /**
     * Extract item amount
     */
    private extractItemAmount(item: unknown): number {
        return this.opayoApplePayService.extractItemAmount(item);
    }

    /**
     * Get shipping methods
     */
    protected getShippingMethods(): ApplePay.IShippingMethod[] {
        return this.opayoApplePayService.buildShippingMethods(this.getExtendedShippingMethods());
    }

    /**
     * Get extended shipping methods from checkout component
     */
    private getExtendedShippingMethods(): OpayoApplePay.IShippingMethodExtended[] | undefined {
        const extendedComponent = this.checkoutFormComponent as OpayoApplePay.ICheckoutFormComponentExtended;
        return extendedComponent?.shippingMethods;
    }

    /**
     * Returns checkout submission data
     * @param {ApplePay.IPaymentEvent} paymentAuthorizedEvent
     * @returns {Checkout.ISubmissionData}
     */
    protected getSubmissionData(paymentAuthorizedEvent: ApplePay.IPaymentEvent): Checkout.ISubmissionData {
        return this.checkoutFormComponent.getSubmissionData();
    }

    /**
     * Check if checkout is virtual
     */
    isVirtual(): boolean {
        return this.checkoutFormComponent?.isVirtual || false;
    }

    /**
     * Clear method called on apple pay close
     */
    protected clear(): void {
        this.inProgressStatus.busy = false;
    }
}
